import type { ReactNode } from "react"

/*
 * The choices a Picker offers, and which of them a search leaves, kept apart from the Picker so the
 * rules can be checked without a browser.
 */

/**
 * One choice. `glyph` leads its row (a Status's circle, an avatar); `secondary` follows its label
 * in the faint tier (a Member's job title); `disabledReason` makes it unchoosable and says why, on
 * a second line; `group` gathers it under a heading with the others that share it. `trailing` is a
 * figure at the row's end, lined up down the list, as a model's price is; `keywords` are more words
 * a search finds it by without showing them, as a model's provider is.
 */
export type PickerOption = {
  id: string
  label: string
  glyph?: ReactNode
  secondary?: string
  disabledReason?: string
  group?: string
  trailing?: string
  keywords?: string
}

/** A Picker offers a search once it has more choices than this; fewer are read at a glance. */
const searchPast = 7

export function isSearchable(options: readonly unknown[]) {
  return options.length > searchPast
}

/** Text as a search compares it: accents taken off, lower case. */
const folded = (text: string) => text.normalize("NFD").replace(/\p{M}/gu, "").toLocaleLowerCase()

/**
 * The choices a search leaves: those whose label, secondary text or keywords hold it, whatever its
 * case and accents. Those whose label holds it come first, so a Member is found by their name
 * before anyone found by their email; each keeps its place among its own.
 */
export function matching<T extends Pick<PickerOption, "label" | "secondary" | "keywords">>(
  options: readonly T[],
  query: string,
) {
  const needle = folded(query.trim())
  if (!needle) return options
  const holds = (text: string | undefined) => text !== undefined && folded(text).includes(needle)
  const byLabel = options.filter(({ label }) => holds(label))
  const byRest = options.filter(
    ({ label, secondary, keywords }) => !holds(label) && (holds(secondary) || holds(keywords)),
  )
  return [...byLabel, ...byRest]
}

/**
 * The choices under their headings, each group where its first choice stood. Choices without a
 * group form one of their own, with no heading.
 */
export function groupsOf<T extends Pick<PickerOption, "group">>(options: readonly T[]) {
  const groups: { heading: string | undefined; options: T[] }[] = []
  for (const option of options) {
    const group = groups.find(({ heading }) => heading === option.group)
    if (group) group.options.push(option)
    else groups.push({ heading: option.group, options: [option] })
  }
  return groups
}

/**
 * The name a Picker that can create offers to make: what is typed, trimmed, when it has something
 * in it and no choice is called exactly that, whatever its case. A name that only begins a choice's
 * is offered beside the choices the search still finds.
 */
export function creatableFrom(options: readonly Pick<PickerOption, "label">[], query: string) {
  const name = query.trim()
  const lower = name.toLocaleLowerCase()
  if (!name || options.some(({ label }) => label.toLocaleLowerCase() === lower)) return undefined
  return name
}
